import { graphql, useStaticQuery } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import React from "react";

import Image from "../../components/image";
import { useTheme } from "../../context/ThemeContext";
import ArrowRight from "../../images/icons/arrow-right.inline.svg";
import { getSourceImageUrlToCGI } from "../../utils/base";
import { Link } from "../../utils/customLink";
import HomeCard from "../cards/homeCard";

const HomeIntro = () => {
  const { page, LeftBgImageLight } = useStaticQuery(graphql`
    query {
      page: wpPage(slug: { eq: "home-page" }) {
        ...HomepageConserveYourPlanetFragment
      }
      LeftBgImageLight: file(name: { eq: "HomeBGImage" }) {
        ...HighQualityFileFragment
        childImageSharp {
          gatsbyImageData(formats: WEBP, quality: 90, layout: FULL_WIDTH)
        }
      }
    }
  `);

  const { colorMode, peopleDomain } = useTheme();

  const { acfConserveYourPlanet } = page ?? {};

  const cypHeadingIntro = acfConserveYourPlanet?.cypHeadingIntro ?? "";
  const cypHeadingContent = acfConserveYourPlanet?.cypHeadingContent ?? "";
  const cypHeadingOutro = acfConserveYourPlanet?.cypHeadingOutro ?? "";
  const cypDescription = acfConserveYourPlanet?.cypDescription ?? "";

  const sidebarTitle = acfConserveYourPlanet?.sidebarTitle ?? "";
  const sidebarContent = acfConserveYourPlanet?.sidebarContent ?? "";
  const cypDescriptionTagline =
    acfConserveYourPlanet?.cypDescriptionTagline ?? "";

  const cypBackgroundImage = {
    gatsbyImage: acfConserveYourPlanet?.cypBackgroundImage?.gatsbyImage ?? null,
    localFile:
      acfConserveYourPlanet?.cypBackgroundImage?.localFile?.publicURL ?? null,
    sourceUrl: acfConserveYourPlanet?.cypBackgroundImage?.sourceUrl
  };
  const sidebarOverlayImage = {
    gatsbyImage:
      acfConserveYourPlanet?.sidebarOverlayImage?.gatsbyImage ?? null,
    localFile:
      acfConserveYourPlanet?.sidebarOverlayImage?.localFile?.publicURL ?? null,
    sourceUrl: acfConserveYourPlanet?.sidebarOverlayImage?.sourceUrl ?? null
  };

  const sidebarBackgroundImage = {
    gatsbyImage:
      acfConserveYourPlanet?.sidebarBackgroundImage?.gatsbyImage ?? null,
    localFile:
      acfConserveYourPlanet?.sidebarBackgroundImage?.localFile?.publicURL ??
      null,
    sourceUrl: acfConserveYourPlanet?.sidebarBackgroundImage?.sourceUrl ?? null
  };

  // console.debug("cypBackgroundImage", cypBackgroundImage);
  return (
    <>
      <section
        id={"home-intro-section"}
        key={"home-intro-section-key"}
        className="!min-h-0"
      >
        {colorMode === "dark" && (
          <section className="relative flex flex-col items-center justify-center px-4 pt-0 md:pt-20 mx-auto md:px-0">
            <div className="flex justify-start absolute top-0 left-0 w-full h-full md:w-[61vw]">
              <StaticImage
                alt="Our Planet Leaf"
                src={"../../images/left-leaf-bg.svg"}
                className="object-cover object-left md:object-center"
              />
            </div>

            <div className="container flex flex-col w-full md:w-10/12">
              <div className="flex flex-col gap-10 md:flex-row">
                <div className="relative w-full md:w-2/3 sm:w-full pt-0 md:pt-[44px]">
                  <div className="relative">
                    <div className="flex flex-col md:flex-row md:justify-end border-t border-b md:mt-0 md:border-none border-t-gray-standard border-b-gray-standard py-8 md:py-0">
                      <div className="flex flex-col md:absolute md:z-20">
                        <div className="md:flex hidden mb-4">
                          <span className="text-white uppercase font-light text-sm tracking-[0.19em]">
                            Planet
                          </span>
                          <ArrowRight className="w-3 h-3 stroke-green-standard gvi-text-green rotate-90 mt-1 ml-2" />
                        </div>

                        <div className="flex flex-col font-body text-[64px] leading-[44px] md:leading-[50px] font-bold text-white">
                          <span className="text-[26px] md:text-[38px] font-light">
                            {cypHeadingIntro}
                          </span>
                          {cypHeadingContent},
                          <span className="text-[26px] md:text-[38px] font-light ">
                            {cypHeadingOutro}.
                          </span>
                        </div>

                        <div className="pt-4 md:py-16 pr-0 md:pr-20">
                          <div
                            className="flex flex-col text-gray-generic text-base leading-[20px] font-light pb-10"
                            dangerouslySetInnerHTML={{ __html: cypDescription }}
                          />
                          <Link
                            className={
                              "submit-button view-program-link group flex gap-2 h-10 justify-center items-center w-full md:w-max px-7 tracking-wider rounded-sm absolute mt-56 md:mt-0"
                            }
                            to="/find-a-program"
                          >
                            <span>Find a program</span>
                            <ArrowRight className="w-3 h-3 stroke-gray-darker dark:group-hover:stroke-green-standard group-hover:stroke-black-lighter mt-1 ml-2" />
                          </Link>
                        </div>
                      </div>

                      <Image
                        imageAlt={""}
                        image={cypBackgroundImage?.gatsbyImage}
                        imageClass="md:absolute md:right-0 md:z-0 md:w-3/4 w-full block rounded-md bg-cover bg-center h-[200px] bg-white object-cover object-center"
                        sourceUrl={cypBackgroundImage?.sourceUrl}
                      />
                    </div>
                  </div>
                </div>

                <div className="relative w-full pt-6 md:pt-0 md:w-1/3 right-gallery-wrapper">
                  <HomeCard
                    sidebarTitle={sidebarTitle}
                    sidebarContent={sidebarContent}
                    sidebarOverlayImage={sidebarOverlayImage}
                    sidebarBackgroundImage={sidebarBackgroundImage}
                    peopleDomain={peopleDomain}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {colorMode === "light" && (
          <div className="w-full flex flex-col md:flex-row">
            <div className="w-full md:w-7/12 flex">
              <div className="relative overflow-hidden w-full">
                <Image
                  imageAlt=""
                  image={LeftBgImageLight}
                  imageClass="w-full h-[830px] md:h-106 object-cover object-center"
                  sourceUrl={LeftBgImageLight?.publicURL}
                />
                <div className="flex flex-col w-11/12 md:w-8/12 px-4 md:mx-auto">
                  <div className="md:flex hidden absolute pt-8 top-0">
                    <span className="text-black-lighter dark:text-white pr-2 uppercase font-semibold text-xs menu-link tracking-[0.19em]">
                      People
                    </span>
                    <ArrowRight className="w-3 h-3 stroke-red-light rotate-90 mt-[2px]" />
                  </div>

                  <div className="mb-4 py-16 top-0 absolute pr-4 md:pr-0">
                    <h2 className="w-full md:w-7/12 font-alice text-black-lighter text-[26px] md:text-[38px] font-light leading-9 md:leading-[50px]">
                      {cypHeadingIntro} <br />{" "}
                      <span className="text-[44px] md:text-[64px] font-semibold font-body">
                        {cypHeadingContent},
                      </span>{" "}
                      <br /> {cypHeadingOutro}.
                    </h2>
                    <div
                      className="py-8 text-black-lighter text-base pr-4 md:pr-24"
                      dangerouslySetInnerHTML={{ __html: cypDescription }}
                    />
                    <div
                      className="pb-6 text-black-lighter text-base pr-24"
                      dangerouslySetInnerHTML={{
                        __html: cypDescriptionTagline
                      }}
                    />

                    <Image
                      imageAlt=""
                      image={cypBackgroundImage?.gatsbyImage}
                      imageClass="flex md:hidden dark:h-[200px] rounded-md object-cover object-center pr-4 mb-6"
                      sourceUrl={getSourceImageUrlToCGI({
                        uri: cypBackgroundImage?.sourceUrl
                      })}
                    />

                    <Link
                      to="/find-a-program"
                      className="flex items-center justify-center gap-3 p-2 w-full md:w-4/12 rounded-full dark:rounded-sm border border-black-lighter font-bold text-black-lighter bg-view-fillbtn-bg"
                    >
                      Find a program
                      <ArrowRight className="w-3 h-3 stroke-black-lighter mt-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full md:w-7/12 hidden md:flex">
              <div className="w-full flex flex-wrap">
                <Image
                  imageAlt=""
                  image={cypBackgroundImage?.gatsbyImage}
                  imageClass="w-full object-cover object-center md:h-[545px]"
                  sourceUrl={getSourceImageUrlToCGI({
                    uri: cypBackgroundImage?.sourceUrl
                  })}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default HomeIntro;
